import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import { useCallback } from 'react';
import { settle, withContext } from '../../Forms/gql/hooks';
import { ENSURE_W9_FORM, REVEAL_FORM_SECRET } from './mutations';
import {
  GET_FORM_SUBMISSION_EVENTS,
  GET_W9_PDF,
  GET_W9_STATS,
  GET_W9_YEARS,
} from './queries';

// Data hooks for the W-9 section (w9-plan §6.6), in the Forms shape:
// queries return the Apollo result plus the unwrapped data under a
// friendly name; mutation-style hooks return an async function resolving
// to exactly one of { data } | { error: { global, fields, code } }.

// ---- Queries ---------------------------------------------------------------

// → { ...result, years: [{ year, count, status }] } newest first
export const useGetW9Years = (formId, options = {}) => {
  const result = useQuery(
    GET_W9_YEARS,
    withContext({
      variables: { form_id: Number(formId) },
      skip: !formId,
      fetchPolicy: 'cache-and-network',
      ...options,
    })
  );
  return { ...result, years: (result.data && result.data.getW9Years) || [] };
};

// → { ...result, stats: W9Stats | null }
export const useGetW9Stats = (formId, taxYear, options = {}) => {
  const result = useQuery(
    GET_W9_STATS,
    withContext({
      variables: { form_id: Number(formId), tax_year: Number(taxYear) },
      skip: !formId || !taxYear,
      fetchPolicy: 'cache-and-network',
      ...options,
    })
  );
  return { ...result, stats: (result.data && result.data.getW9Stats) || null };
};

// → { ...result, events: [{ id, kind, user_name, meta, created_at }] }
export const useGetFormSubmissionEvents = (
  submissionId,
  { skip = false, ...options } = {}
) => {
  const result = useQuery(
    GET_FORM_SUBMISSION_EVENTS,
    withContext({
      variables: { submission_id: Number(submissionId) },
      skip: skip || !submissionId,
      fetchPolicy: 'cache-and-network',
      ...options,
    })
  );
  return {
    ...result,
    events: (result.data && result.data.getFormSubmissionEvents) || [],
  };
};

// (submissionId) => Promise<{ url, filename, expires_at } | { error }>
// Imperative like useFormFileDownloadUrl: the link is short-lived and every
// call is logged, so it is never read from the cache.
export const useGetW9Pdf = () => {
  const client = useApolloClient();
  return useCallback(
    async (submissionId) => {
      const { data, error } = await settle(
        client.query(
          withContext({
            query: GET_W9_PDF,
            variables: { submission_id: Number(submissionId) },
            fetchPolicy: 'network-only',
          })
        ),
        'getW9Pdf'
      );
      return error ? { error } : { ...data };
    },
    [client]
  );
};

// ---- Mutations -------------------------------------------------------------

// (accountId) => { data: BuilderForm } | { error }
export const useEnsureW9Form = () => {
  const [mutate] = useMutation(ENSURE_W9_FORM, withContext());
  return useCallback(
    (accountId) =>
      settle(
        mutate({ variables: { input: { account_id: Number(accountId) } } }),
        'ensureW9Form'
      ),
    [mutate]
  );
};

// (submissionId, fieldId) => { data: '123-45-6789' } | { error }
// no-cache: the full TIN must never sit in the Apollo cache (T11).
export const useRevealFormSecret = () => {
  const [mutate] = useMutation(
    REVEAL_FORM_SECRET,
    withContext({ fetchPolicy: 'no-cache' })
  );
  return useCallback(
    (submissionId, fieldId) =>
      settle(
        mutate({
          variables: {
            input: { submission_id: Number(submissionId), field_id: fieldId },
          },
        }),
        'revealFormSecret'
      ),
    [mutate]
  );
};
